import { useTheme } from "@/components/ThemeProvider";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  sandboxArtifactDownloadOptions,
  sandboxArtifactPreviewOptions,
} from "@/lib/data/react-query/sandbox-artifacts";
import { isDarkTheme } from "@/lib/themes";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Download, LoaderCircle, Maximize2 } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { visualDocument } from "./chat-visual-document";

function VisualFrame({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: (expanded: boolean) => ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const [visible, setVisible] = useState(false);
  const download = useMutation(sandboxArtifactDownloadOptions(id));
  const downloadVisual = async () => {
    try {
      const result = await download.mutateAsync("attachment");
      const anchor = document.createElement("a");
      anchor.href = result.url;
      anchor.download = "";
      anchor.rel = "noopener noreferrer";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } catch {
      // The mutation state displays a retryable error beside the visual.
    }
  };
  const downloadButton = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      aria-label={`Download ${title}`}
      disabled={download.isPending}
      onClick={() => void downloadVisual()}
    >
      {download.isPending ? (
        <LoaderCircle className="size-4 animate-spin" />
      ) : (
        <Download className="size-4" />
      )}
      {download.isPending ? "Downloading…" : "Download"}
    </Button>
  );
  const downloadError = download.isError ? (
    <span role="alert" className="text-destructive block px-4 py-2 text-sm">
      This visual could not be downloaded. Try again.
    </span>
  ) : null;
  const element = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!element.current) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(element.current);
    return () => {
      observer.disconnect();
    };
  }, []);
  return (
    <span
      ref={element}
      className="border-border bg-background my-4 block min-w-0 overflow-hidden rounded-xl border"
      data-chat-visual
    >
      <span className="border-border flex items-center justify-between gap-3 border-b px-4 py-2 text-sm">
        <span className="truncate font-medium">{title}</span>
        <span className="flex shrink-0 items-center gap-2">
          {downloadButton}
          <button
            type="button"
            aria-label={`Expand ${title}`}
            className="text-muted-foreground hover:text-foreground rounded p-1"
            onClick={() => {
              setExpanded(true);
            }}
          >
            <Maximize2 size={16} />
          </button>
        </span>
      </span>
      {!expanded && downloadError}
      {visible ? children(false) : <span className="block h-[420px]" />}
      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="max-w-[95vw]" aria-describedby={undefined}>
          <span className="flex min-w-0 items-center justify-between gap-3 pr-6">
            <DialogTitle className="truncate">{title}</DialogTitle>
            {downloadButton}
          </span>
          {downloadError}
          {expanded ? children(true) : null}
        </DialogContent>
      </Dialog>
    </span>
  );
}

function ArtifactPreview({
  id,
  title,
  expanded,
}: {
  id: string;
  title: string;
  expanded: boolean;
}) {
  const { theme } = useTheme();
  const preview = useQuery(sandboxArtifactPreviewOptions(id));
  if (preview.isPending)
    return (
      <span
        className={`text-muted-foreground block p-4 text-sm ${expanded ? "h-[75vh]" : "h-[420px]"}`}
      >
        Loading visual…
      </span>
    );
  if (preview.isError)
    return (
      <span role="status" className="text-muted-foreground block p-4 text-sm">
        This visual is unavailable. It may have been removed or you may no
        longer have access.
      </span>
    );
  return (
    <span className="block">
      <iframe
        title={title}
        sandbox=""
        referrerPolicy="no-referrer"
        tabIndex={expanded ? 0 : -1}
        srcDoc={visualDocument(preview.data, isDarkTheme(theme), expanded)}
        className={`block w-full border-0 ${expanded ? "h-[75vh]" : "pointer-events-none h-[420px]"}`}
      />
      {/<script\b/i.test(preview.data) ? (
        <span className="text-muted-foreground block px-4 py-2 text-xs">
          Preview shows static content. Script-based interactions are not
          available.
        </span>
      ) : null}
    </span>
  );
}

export function ChatArtifactVisual({
  id,
  title,
}: {
  id: string;
  title: string;
}) {
  return (
    <VisualFrame id={id} title={title || "Visual"}>
      {(expanded) => (
        <ArtifactPreview
          id={id}
          title={title || "Visual"}
          expanded={expanded}
        />
      )}
    </VisualFrame>
  );
}
